import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Fingerprint, Lock } from "lucide-react";
import { isNativeApp } from "@/lib/native/platform";
import {
  checkBiometricAvailability,
  isBiometricLockEnabled,
  verifyBiometric,
} from "@/lib/native/biometric";
import { getAdminSessionItem } from "@/lib/adminSessionStore";
import hibaneLogoSignin from "@/assets/hibane-logo-signin.png";

/** Re-lock after the app has been in the background for longer than this. */
const RELOCK_AFTER_MS = 60_000;

/**
 * Biometric app lock for the native shell.
 *
 * Renders a full-screen lock over the app when:
 *  - we're inside the Capacitor native shell, AND
 *  - the user enabled biometric lock on this device, AND
 *  - an admin session actually exists (nothing to protect otherwise).
 *
 * On the web and the installed PWA this component renders nothing at all.
 */
export const NativeBiometricLock = () => {
  const [locked, setLocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [label, setLabel] = useState("Biometric unlock");
  const backgroundedAt = useRef<number | null>(null);

  const hasSession = () => Boolean(getAdminSessionItem("admin_session_token"));

  const attemptUnlock = useCallback(async () => {
    setVerifying(true);
    const ok = await verifyBiometric("Unlock HIBANE Desk");
    setVerifying(false);
    if (ok) setLocked(false);
  }, []);

  const engageLock = useCallback(async () => {
    if (!isNativeApp() || !hasSession()) return;
    if (!(await isBiometricLockEnabled())) return;
    const info = await checkBiometricAvailability();
    if (!info.available) return; // biometry removed on device — don't trap the user out
    setLabel(info.label);
    setLocked(true);
  }, []);

  // Lock on cold start.
  useEffect(() => {
    if (!isNativeApp()) return;
    void (async () => {
      await engageLock();
    })();
  }, [engageLock]);

  // Auto-prompt as soon as the lock appears.
  useEffect(() => {
    if (locked && !verifying) {
      void attemptUnlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  // Re-lock when the app returns from a long background stint.
  useEffect(() => {
    if (!isNativeApp()) return;
    let remove: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          if (!isActive) {
            backgroundedAt.current = Date.now();
            return;
          }
          const away = backgroundedAt.current ? Date.now() - backgroundedAt.current : 0;
          backgroundedAt.current = null;
          if (away >= RELOCK_AFTER_MS) void engageLock();
        });
        remove = () => void handle.remove();
      } catch (err) {
        console.warn("[native] app state listener unavailable", err);
      }
    })();
    return () => remove?.();
  }, [engageLock]);

  if (!locked) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-background px-8">
      <img src={hibaneLogoSignin} alt="HIBANE" className="h-16 w-auto object-contain" />
      <div className="flex flex-col items-center gap-2 text-center">
        <Lock className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-lg font-semibold text-foreground">HIBANE Desk is locked</h1>
        <p className="text-sm text-muted-foreground">
          Verify with {label} to continue where you left off.
        </p>
      </div>
      <Button onClick={() => void attemptUnlock()} disabled={verifying} className="w-full max-w-xs gap-2">
        <Fingerprint className="h-4 w-4" aria-hidden="true" />
        {verifying ? "Waiting for verification…" : `Unlock with ${label}`}
      </Button>
    </div>
  );
};

export default NativeBiometricLock;
